import { type MutableRefObject, useCallback } from 'react'

import { createCreation } from '../api/creations'
import { isApiError } from '../api/errors'
import type { ClientUiPublic } from '../api/platformConfig'
import type { ToastShowOptions } from '../context/ToastContext'
import { effectiveAspectRatios, resolveClientUiImageModel } from '../lib/clientUiImageModel'
import {
  CREATE_PAGE_GENERATION_DEFER_MS,
  normalizeCreationTaskError,
  waitCreatePageDeferMs,
} from '../lib/createPageTimelinePresentation'
import { waitForCreationTask } from '../lib/creationTaskLifecycle'
import { timelineAtFromBackendIso } from '../lib/dateDisplay'
import { ensureReferenceImagesLocal } from '../lib/ensureReferenceImagesLocal'
import { buildImageCreationOptions } from '../lib/imageCreationOptions'
import { validateImageGenParams } from '../lib/imageGenValidation'
import { shortenPrompt } from '../lib/promptTruncate'
import { timelineExpectedImageCount } from '../lib/timelineImageCount'
import { mergeTerminalCreationIntoRound } from '../lib/timelineTaskMerge'
import type { TimelineRound } from '../types/timeline'

export type CreatePageGenerationCapture = {
  text: string
  refSnapshot: string[]
  label: string
  sizeLabel: string
  count: number
  /**
   * 该轮次提交时的画幅比例。追加生成 / 重新生成必须沿用**旧轮次快照**，
   * 不能用当前表单值：否则界面上写 9:16、实际请求发的是 16:9 或 auto（上游兜底 1:1）。
   */
  aspectRatio?: string
  /** 该轮次提交时的额外参数快照（quality / format / n 等），同样不能被当前表单覆盖 */
  extraParamValues?: Record<string, string>
  /**
   * 该轮次提交时的画幅模式。旧轮次快照只有 size 时必须走 size 模式，
   * 不能套用当前模型的 aspect_ratio 模式（否则发空比例、上游按默认画幅出图）。
   */
  dimensionMode?: 'size' | 'aspect_ratio'
  /**
   * 该轮次提交时的精确尺寸（size 模式）。快照 size 缺失或模型尺寸清单已变化时，
   * 由调用方从快照宽高推导回填；未传则回读轮次快照的 size。
   */
  sizeValue?: string
}

type UseCreatePageGenerationSubmitOptions = {
  platformId: string
  modelId: string
  apiSize: string
  aspectRatio: string
  extraParamValues: Record<string, string>
  outputSuperResolution: boolean
  referenceSuperResolutionAvailable: boolean
  clientUi: ClientUiPublic
  upsertRound: (round: TimelineRound) => void
  showToast: (options: ToastShowOptions) => string
  deferringRoundIdRef: MutableRefObject<string | null>
  generationDeferControllerRef: MutableRefObject<AbortController | null>
}

/** 追加生成场景：骨架挂在原轮次上，终态回写后按 taskId 移除 */
export type CreatePageAppendContext = {
  skeletonId: string
}

export function useCreatePageGenerationSubmit({
  platformId,
  modelId,
  apiSize,
  aspectRatio,
  extraParamValues,
  outputSuperResolution,
  referenceSuperResolutionAvailable,
  clientUi,
  upsertRound,
  showToast,
  deferringRoundIdRef,
  generationDeferControllerRef,
}: UseCreatePageGenerationSubmitOptions) {
  const executeCreationAndFinalize = useCallback(
    async (
      baseRound: TimelineRound,
      capture: CreatePageGenerationCapture,
      append?: CreatePageAppendContext,
    ) => {
      const isAppend = append != null
      // 追加生成不能连坐整轮：骨架只是临时载体，出错时只丢骨架，保留原图与原状态
      const dropAppendSkeleton = () => {
        if (!append) return
        upsertRound({
          ...baseRound,
          pendingItems: (baseRound.pendingItems ?? []).filter(
            (s) => s.id !== append.skeletonId,
          ),
          pendingSubmitDeadline: undefined,
        })
      }
      const { text, refSnapshot } = capture
      const taskPlatformId = baseRound.platform ?? platformId
      const taskModelId = baseRound.modelId || modelId
      // size 同样以「本次提交快照」优先：模型尺寸清单变化时由调用方按旧轮次宽高推导回填
      const taskApiSize = capture.sizeValue?.trim() || baseRound.params?.size || apiSize
      const taskModel = resolveClientUiImageModel(clientUi, taskModelId, taskPlatformId)
      // 轮次快照优先：旧轮次只有 size 时按 size 模式发，避免发空比例被上游按默认画幅兜底
      const dimensionMode = capture.dimensionMode ?? taskModel?.dimensionMode ?? 'size'
      const selectionCheck = validateImageGenParams(taskPlatformId, taskModelId, taskApiSize, dimensionMode)
      if (!selectionCheck.ok) {
        if (isAppend) {
          dropAppendSkeleton()
        } else {
          upsertRound({
            ...baseRound,
            status: 'FAILED',
            errorMessage: selectionCheck.message,
            pendingSubmitDeadline: undefined,
          })
        }
        showToast({ message: selectionCheck.message, variant: 'error' })
        return
      }
      // 追加只认本次请求的张数（原轮次 params.n 属于原生成，不能拿来裁新结果）
      const imageCount = isAppend
        ? Math.max(1, capture.count || 1)
        : timelineExpectedImageCount(baseRound)
      const roundId = baseRound.id
      let createdTaskId: string | undefined
      // 未拿到任务号前不伪造任务标识；拿到后统一以「任务 #publicId 」作为提示主语。
      let taskLabel = '生图'
      const taskAspectOptions = effectiveAspectRatios(clientUi, taskModelId, taskPlatformId)
      // 优先用调用方传入的「轮次快照」；仅在普通提交（无快照）时回落到当前表单值。
      const taskAspectRatio = capture.aspectRatio ?? aspectRatio
      const taskExtraParamValues = capture.extraParamValues ?? extraParamValues
      // 追加/重试必须拿得到画幅参数：缺比例就发空值，上游会按默认画幅兜底出图
      // （表现为时间线标 16:9、实际出 9:16），宁可显式失败也不静默跑偏。
      if (isAppend) {
        const missingDimension =
          dimensionMode === 'aspect_ratio'
            ? !String(taskAspectRatio ?? '').trim()
            : !String(taskApiSize ?? '').trim()
        if (missingDimension) {
          dropAppendSkeleton()
          showToast({
            message: '本轮缺少画幅快照，无法追加生成；请重新生成该轮次',
            variant: 'error',
          })
          return
        }
      }
      const opts = buildImageCreationOptions({
        platformId: taskPlatformId,
        apiSize: taskApiSize,
        aspectOptions: taskAspectOptions,
        aspectRatio: taskAspectRatio,
        extraParamValues: taskExtraParamValues,
        dimensionMode,
      })
      try {
        // 参考图守门检测：拦截过期 CDN URL，放行本地路径和有效外链
        const refEnsure = await ensureReferenceImagesLocal(refSnapshot)
        if (refEnsure.errorMessage) {
          if (isAppend) {
            dropAppendSkeleton()
          } else {
            upsertRound({
              ...baseRound,
              status: 'FAILED',
              errorMessage: refEnsure.errorMessage,
              pendingSubmitDeadline: undefined,
            })
          }
          showToast({
            message: `${taskLabel}失败：${refEnsure.errorMessage}`,
            variant: 'error',
            autoCloseMs: 0,
          })
          return
        }
        // 以 roundId 作为幂等键：同一生成意图（含超时后重试）复用同一个键，
        // 后端按「用户 + 幂等键」去重，直接接回原任务，避免重复生成与重复扣费。
        const createdTask = await createCreation(
          {
            platform: taskPlatformId,
            mode: 'TEXT_TO_IMAGE',
            prompt: text,
            model: taskModelId,
            options: opts,
            referenceImages: refEnsure.refs.length ? refEnsure.refs : undefined,
            superResolution:
              outputSuperResolution && referenceSuperResolutionAvailable,
          },
          // 追加生成必须用骨架 id 做幂等键：沿用 roundId 会被后端判为「同一生成意图」，
          // 直接接回原轮次那次任务，导致追加出的还是旧图。
          { idempotencyKey: append?.skeletonId ?? roundId },
        )
        taskLabel = `任务 #${createdTask.publicId} `
        createdTaskId = createdTask.publicId
        // 追踪回填后的 pendingItems（避免后续合并时读到未回填的旧快照）
        let resolvedPendingItems = baseRound.pendingItems
        if (isAppend && append) {
          resolvedPendingItems = (baseRound.pendingItems ?? []).map((s) =>
            s.id === append.skeletonId
              ? { ...s, creationTaskId: createdTask.publicId }
              : s,
          )
          upsertRound({
            ...baseRound,
            pendingItems: resolvedPendingItems,
            pendingSubmitDeadline: undefined,
          })
        } else {
          upsertRound({ ...baseRound, taskId: createdTask.publicId, pendingSubmitDeadline: undefined })
        }
        const task = await waitForCreationTask(createdTask)

        if (task.status !== 'SUCCEEDED') {
          const reason = normalizeCreationTaskError(task, null)
          if (isAppend && append) {
            // 追加失败：丢骨架，原轮次保持成功态，不写 FAILED
            upsertRound({
              ...baseRound,
              pendingItems: (baseRound.pendingItems ?? []).filter(
                (s) => s.id !== append.skeletonId,
              ),
              pendingSubmitDeadline: undefined,
            })
          } else {
            upsertRound({
              ...baseRound,
              taskId: task.publicId,
              status: 'FAILED',
              errorMessage: reason,
              pendingSubmitDeadline: undefined,
            })
          }
          showToast({
            message: `${taskLabel}失败：${reason}`,
            variant: 'error',
            autoCloseMs: 0,
          })
          return
        }

        const resolvedAt = timelineAtFromBackendIso(task.createdAt) ?? baseRound.at
        const merged = mergeTerminalCreationIntoRound(
          {
            ...baseRound,
            at: resolvedAt,
            prompt: text,
            promptPreview: shortenPrompt(text),
            pendingSubmitDeadline: undefined,
            // 追加场景用回填过 creationTaskId 的 pendingItems（stale closure 修复）
            ...(isAppend ? { pendingItems: resolvedPendingItems } : {}),
          },
          task,
          imageCount,
          isAppend,
        )
        // 终态既非成功也非失败（如后台取消）：不静默丢弃，如实标记并提示。
        if (!merged) {
          if (isAppend && append) {
            upsertRound({
              ...baseRound,
              pendingItems: (baseRound.pendingItems ?? []).filter(
                (s) => s.id !== append.skeletonId,
              ),
              pendingSubmitDeadline: undefined,
            })
          } else {
            upsertRound({
              ...baseRound,
              taskId: task.publicId,
              status: 'FAILED',
              errorMessage: `任务状态异常（${task.status}）`,
              pendingSubmitDeadline: undefined,
            })
          }
          showToast({
            message: `${taskLabel}未返回可用结果（状态 ${task.status}）`,
            variant: 'error',
            autoCloseMs: 0,
          })
          return
        }
        upsertRound(merged)
        if (merged.status === 'SUCCEEDED') {
          showToast({
            message: isAppend
              ? `${taskLabel}已完成，已追加到原轮次`
              : `${taskLabel}已完成`,
            variant: 'success',
            autoCloseMs: 4000,
          })
          return
        }
        showToast({
          message:
            merged.status === 'UNAVAILABLE'
              ? `${taskLabel}生成完成，但图片未入库`
              : `${taskLabel}未完成`,
          variant: 'warning',
          autoCloseMs: 6500,
        })
      } catch (error) {
        // 结果未知（已拿到任务 ID，但等待超时 / 网络中断）：
        // 写操作绝不自动重试——重试会重复生成、重复扣费。
        // round 保持 PENDING 并保留 taskId，交由创作记录 / 重入对账确认终态，
        // 绝不标 FAILED、绝不删除。
        if (createdTaskId) {
          if (isAppend && append) {
            // 结果未知：骨架也一并清掉，避免永久占位；图片由创作记录兜底
            upsertRound({
              ...baseRound,
              pendingItems: (baseRound.pendingItems ?? []).filter(
                (s) => s.id !== append.skeletonId,
              ),
              pendingSubmitDeadline: undefined,
            })
          } else {
            upsertRound({
              ...baseRound,
              taskId: createdTaskId,
              status: 'PENDING',
              errorMessage:
                '生成提交已发送，结果待确认（等待超时或网络中断），请稍后在创作记录查看',
              pendingSubmitDeadline: undefined,
            })
          }
          showToast({
            message: `${taskLabel}结果待确认：等待超时或网络中断，请稍后在创作记录查看`,
            variant: 'warning',
            autoCloseMs: 0,
          })
          return
        }
        // 未拿到任务 ID 也要区分两类情况：
        // 1) 服务端明确拒绝（4xx，如参数非法、鉴权失败）——确定性失败，标 FAILED；
        // 2) 网络中断 / 网关超时 / 5xx——请求可能已被受理，结果未知，绝不标 FAILED，
        //    保持 PENDING 并用同一幂等键（roundId）重提，由后端按「用户 + 幂等键」去重接回原任务。
        const rejectedByServer =
          isApiError(error) &&
          typeof error.status === 'number' &&
          error.status >= 400 &&
          error.status < 500 &&
          error.status !== 408 &&
          error.status !== 429
        if (!rejectedByServer) {
          if (isAppend && append) {
            dropAppendSkeleton()
          } else {
            upsertRound({
              ...baseRound,
              status: 'PENDING',
              errorMessage: '提交结果未确认（网络中断或超时），请稍后重试；重复提交不会重复生成',
              pendingSubmitDeadline: undefined,
            })
          }
          showToast({
            message: `${taskLabel}提交结果未确认（网络中断或超时）`,
            variant: 'warning',
            autoCloseMs: 0,
          })
          return
        }
        const reason = normalizeCreationTaskError(
          {
            publicId: roundId,
            platform: platformId,
            mode: 'TEXT_TO_IMAGE',
            prompt: text,
            model: modelId,
            referenceImages: refSnapshot,
            options: {},
            status: 'FAILED',
            result: null,
            errorMessage: error instanceof Error ? error.message : null,
            createdAt: '',
            updatedAt: '',
          },
          error,
        )
        if (isAppend) {
          dropAppendSkeleton()
        } else {
          upsertRound({
            ...baseRound,
            status: 'FAILED',
            errorMessage: reason,
            pendingSubmitDeadline: undefined,
          })
        }
        showToast({
          message: `${taskLabel}失败：${reason}`,
          variant: 'error',
          autoCloseMs: 0,
        })
      } finally {
        deferringRoundIdRef.current = null
        generationDeferControllerRef.current = null
      }
    },
    [
      platformId,
      modelId,
      apiSize,
      aspectRatio,
      extraParamValues,
      outputSuperResolution,
      referenceSuperResolutionAvailable,
      upsertRound,
      showToast,
      clientUi,
      deferringRoundIdRef,
      generationDeferControllerRef,
    ],
  )

  const submitRoundWithDefer = useCallback(
    async (
      placeholderRound: TimelineRound,
      capture: CreatePageGenerationCapture,
      append?: CreatePageAppendContext,
    ) => {
      generationDeferControllerRef.current?.abort()
      const deferAc = new AbortController()
      generationDeferControllerRef.current = deferAc
      // 沿用 roundId：页面「取消发送」按钮按 roundId 判定，
      // 追加场景取消时由下面的分支只丢骨架、不连坐整轮
      deferringRoundIdRef.current = placeholderRound.id
      try {
        await waitCreatePageDeferMs(CREATE_PAGE_GENERATION_DEFER_MS, deferAc.signal)
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          if (append) {
            // 追加取消：只丢骨架，整轮保持成功态与原图
            upsertRound({
              ...placeholderRound,
              pendingItems: (placeholderRound.pendingItems ?? []).filter(
                (s) => s.id !== append.skeletonId,
              ),
              pendingSubmitDeadline: undefined,
            })
          } else {
            upsertRound({
              ...placeholderRound,
              pendingSubmitDeadline: undefined,
              status: 'CANCELLED',
              errorMessage: '已在发送前取消',
            })
          }
          deferringRoundIdRef.current = null
          generationDeferControllerRef.current = null
          return
        }
        throw err
      }
      const readyRound = { ...placeholderRound, pendingSubmitDeadline: undefined }
      upsertRound(readyRound)
      await executeCreationAndFinalize(readyRound, capture, append)
    },
    [
      executeCreationAndFinalize,
      upsertRound,
      deferringRoundIdRef,
      generationDeferControllerRef,
    ],
  )

  return { executeCreationAndFinalize, submitRoundWithDefer }
}
